import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Chip, Dialog, IconButton, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  TableScroller,
  Tabs,
  useAsync,
  useNotice,
} from '../../../components/common';
import { useBuddyWrite } from '../../api/client';
import {
  accessFor,
  classesOf,
  type AccessApi,
  type AccessEffective,
  type AccessProfile,
  type AccessScope,
} from '../../api/access';
import type { Organization, System, Tenant } from '../../api/setup';
import { messageOf, useQueryParam, useSetup } from './shared';

/**
 * Data access (buddy3 ADR-0020): which NYCE.LOGIC groups may read monetary values, personal data, … Three things,
 * each administered once — the classes on the data (buddy3's repo), the profiles (shipped + custom) and, per customer,
 * the group → profile mapping. "Test as" shows what a user in some groups gets on one system and what it hides.
 */
export function BuddyAccessPage() {
  const { target, api: setup } = useSetup();
  const access = useMemo(() => accessFor(target), [target]);
  const canWrite = useBuddyWrite();
  const profiles = useAsync(() => access.profiles(), [access]);
  const lists = useAsync(async () => {
    const [organizations, systems, tenants] = await Promise.all([
      setup.organizations.list(),
      setup.systems.list(),
      setup.tenants.list(),
    ]);
    return { organizations, systems, tenants };
  }, [setup]);
  const toast = useNotice();

  return (
    <Page
      title="Data access"
      description={
        <>
          Who may read monetary values and personal data. Map the customer’s own NYCE.LOGIC groups to a
          profile; a user gets everything any of their mapped groups allows, <code>*</code> covers everyone
          else, and with nothing mapped every user keeps full access. Passwords, MFA and licences are never
          readable.
        </>
      }
    >
      <ErrorBox error={profiles.error ?? lists.error} />
      {toast.element}
      <Loading loading={(profiles.loading && !profiles.data) || (lists.loading && !lists.data)} />
      {profiles.data && lists.data && (
        <>
          <MappingCard
            api={access}
            profiles={profiles.data.profiles}
            known={profiles.data.classes}
            organizations={lists.data.organizations}
            systems={lists.data.systems}
            tenants={lists.data.tenants}
            canWrite={canWrite}
            onSaved={(m) => toast.success(m)}
          />
          <TestAsCard api={access} systems={lists.data.systems} />
          <ProfilesCard
            api={access}
            profiles={profiles.data.profiles}
            known={profiles.data.classes}
            canWrite={canWrite}
            reload={profiles.reload}
            onSaved={(m) => toast.success(m)}
          />
        </>
      )}
    </Page>
  );
}

// ------------------------------------------------------------------------------------------------------------ mapping

const SCOPE_TABS: { value: AccessScope; label: string }[] = [
  { value: 'organization', label: 'Organizations' },
  { value: 'system', label: 'Systems' },
  { value: 'tenant', label: 'Tenants' },
  { value: 'global', label: 'Global' },
];

interface Row {
  group: string;
  profile: string;
}

/** The PUT rows of a draft, or why it cannot be saved. */
export function mappingRows(rows: Row[]): Row[] | string {
  const out = rows
    .map((r) => ({ group: r.group.trim(), profile: r.profile }))
    .filter((r) => r.group || r.profile);
  if (out.some((r) => !r.group)) return 'Every row needs a group (or * for everyone not in a mapped group).';
  if (out.some((r) => !r.profile)) return 'Every row needs a profile.';
  const seen = new Set<string>();
  for (const r of out) {
    const k = r.group.toLowerCase();
    if (seen.has(k)) return `Group ${r.group} is mapped twice.`;
    seen.add(k);
  }
  return out;
}

function MappingCard({
  api,
  profiles,
  known,
  organizations,
  systems,
  tenants,
  canWrite,
  onSaved,
}: {
  api: AccessApi;
  profiles: AccessProfile[];
  known: string[];
  organizations: Organization[];
  systems: System[];
  tenants: Tenant[];
  canWrite: boolean;
  onSaved: (message: string) => void;
}) {
  const [scopeRaw, setScope] = useQueryParam('scope');
  const [scopeIdRaw, setScopeId] = useQueryParam('scopeId');
  const scope: AccessScope = (SCOPE_TABS.find((t) => t.value === scopeRaw)?.value ??
    'organization') as AccessScope;
  const choices = useMemo(() => {
    const list =
      scope === 'organization'
        ? organizations.map((o) => ({ value: o.id, label: `${o.name} (${o.id})` }))
        : scope === 'system'
          ? systems.map((s) => ({
              value: s.id,
              label: `${s.name} (${s.id})${s.organizationName ? ` · ${s.organizationName}` : ''}`,
            }))
          : scope === 'tenant'
            ? tenants.map((t) => ({ value: t.id, label: `${t.name} (${t.id})` }))
            : [];
    return list;
  }, [scope, organizations, systems, tenants]);
  const scopeId =
    scope === 'global'
      ? ''
      : choices.some((c) => c.value === scopeIdRaw)
        ? scopeIdRaw
        : (choices[0]?.value ?? '');
  const all = useAsync(() => api.mappings(), [api]);
  const [rows, setRows] = useState<Row[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const here = useMemo(
    () =>
      (all.data ?? []).filter((m) => m.scope === scope && m.scopeId.toLowerCase() === scopeId.toLowerCase()),
    [all.data, scope, scopeId]
  );
  useEffect(() => {
    setRows(here.map((m) => ({ group: m.group, profile: m.profile })));
    setDirty(false);
    setError(null);
  }, [here]);

  // The wider rows that also apply here (global; a system's tenant and organization) — what an unmapped group falls back to.
  const wider = useMemo(() => {
    const sys = scope === 'system' ? systems.find((s) => s.id === scopeId) : undefined;
    return (all.data ?? []).filter(
      (m) =>
        (m.scope === 'global' && scope !== 'global') ||
        (sys && m.scope === 'organization' && m.scopeId === sys.organizationId) ||
        (sys && m.scope === 'tenant' && m.scopeId === sys.tenantId)
    );
  }, [all.data, scope, scopeId, systems]);

  const profileOptions = profiles.map((p) => ({
    value: p.key,
    label: `${p.name} — ${classesOf(p, known).join(', ') || 'no classes'}`,
  }));
  const name = (key: string) => profiles.find((p) => p.key === key)?.name ?? key;
  const ro = !canWrite;
  const set = (i: number, patch: Partial<Row>) => {
    setRows((cur) => cur.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const save = async () => {
    const body = mappingRows(rows);
    if (typeof body === 'string') return setError(body);
    setBusy(true);
    try {
      await api.putMappings(scope, scopeId, body);
      onSaved(`Saved the group mapping of ${scope === 'global' ? 'every system' : scopeId}`);
      await all.reload();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nyk-card" data-testid="access-mapping">
      <div className="nyk-card-head">
        <h2>Group mapping</h2>
        <Tabs
          value={scope}
          onChange={(v) => {
            setScope(v === 'organization' ? null : v);
            setScopeId(null);
          }}
          options={SCOPE_TABS}
          aria-label="Mapping level"
        />
      </div>
      <p className="nyk-ui-hint">
        Per group the most specific level wins (system before organization before tenant before global).{' '}
        <code>*</code> = everyone in no mapped group here. Saving restarts nyce.buddy’s runtimes.
      </p>
      {scope !== 'global' && (
        <Select
          label={SCOPE_TABS.find((t) => t.value === scope)!.label.replace(/s$/, '')}
          value={scopeId}
          onChange={(id) => setScopeId(id)}
          options={choices}
          searchable
        />
      )}
      <ErrorBox error={all.error ?? error} />
      <Loading loading={all.loading && !all.data} />
      {(scope === 'global' || scopeId) && all.data && (
        <>
          {!rows.length && (
            <EmptyState
              title="Nothing mapped here."
              hint={
                wider.length
                  ? 'The wider levels below decide.'
                  : 'Every user keeps full access until a group (or *) is mapped.'
              }
            />
          )}
          {!!rows.length && (
            <TableScroller>
              <table className="nyk-ui-table" aria-label="Group mapping">
                <thead>
                  <tr>
                    <th>NYCE.LOGIC group</th>
                    <th>Profile</th>
                    {!ro && <th aria-label="Remove" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-mapping={r.group}>
                      <td>
                        {ro ? (
                          <code>{r.group}</code>
                        ) : (
                          <TextField
                            aria-label={`Group ${i + 1}`}
                            value={r.group}
                            placeholder="WMS_FINANCE or *"
                            onChange={(e) => set(i, { group: e.target.value })}
                          />
                        )}
                      </td>
                      <td>
                        {ro ? (
                          name(r.profile)
                        ) : (
                          <Select
                            aria-label={`Profile of ${r.group || `row ${i + 1}`}`}
                            value={r.profile}
                            onChange={(v) => set(i, { profile: v })}
                            options={profileOptions}
                            placeholder="Select the profile…"
                          />
                        )}
                      </td>
                      {!ro && (
                        <td>
                          <IconButton
                            label={`Remove ${r.group || `row ${i + 1}`}`}
                            onClick={() => {
                              setRows((cur) => cur.filter((_, j) => j !== i));
                              setDirty(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          )}
          {!!wider.length && (
            <div className="nyk-ui-hint" data-testid="access-wider">
              Also applies here:{' '}
              {wider.map((m, i) => (
                <span key={`${m.scope}:${m.scopeId}:${m.group}`}>
                  {i > 0 && ', '}
                  <code>{m.group}</code> → {name(m.profile)} ({m.scope}
                  {m.scopeId ? ` ${m.scopeId}` : ''})
                </span>
              ))}
            </div>
          )}
          {!ro && (
            <div className="bd-access-actions">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setRows((cur) => [...cur, { group: '', profile: '' }]);
                  setDirty(true);
                }}
              >
                <Plus size={14} /> Add group
              </Button>
              <Button size="sm" onClick={() => void save()} loading={busy} disabled={!dirty}>
                Save mapping
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------------ test as

function TestAsCard({ api, systems }: { api: AccessApi; systems: System[] }) {
  const [systemId, setSystemId] = useState(systems[0]?.id ?? '');
  const [groups, setGroups] = useState('NYCEBUDDY');
  const [result, setResult] = useState<AccessEffective | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api.effective(
          systemId,
          groups
            .split(',')
            .map((g) => g.trim())
            .filter(Boolean)
        )
      );
    } catch (e) {
      setError(messageOf(e));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="nyk-card" data-testid="access-test">
      <div className="nyk-card-head">
        <h2>Test as</h2>
        <span className="nyk-ui-hint">
          What a user in these groups gets, from the mapping the system is running with.
        </span>
      </div>
      <div className="bd-access-test">
        <Select
          label="System"
          value={systemId}
          onChange={setSystemId}
          options={systems.map((s) => ({ value: s.id, label: `${s.name} (${s.id})` }))}
          searchable
        />
        <TextField
          label="Groups"
          value={groups}
          onChange={(e) => setGroups(e.target.value)}
          hint="comma-separated, as the token names them"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void run();
          }}
        />
        <Button onClick={() => void run()} loading={busy} disabled={!systemId}>
          Test
        </Button>
      </div>
      <ErrorBox error={error} />
      {result && (
        <div className="bd-access-result" data-testid="access-result">
          <div>
            Profile{result.profiles.length === 1 ? '' : 's'}: <b>{result.profiles.join(', ') || '—'}</b>{' '}
            <span className="nyk-ui-hint">
              {result.defaulted
                ? 'no group of this user is mapped: the default decides'
                : `through ${result.matchedGroups.join(', ')}`}
            </span>
          </div>
          {result.note && <div className="nyk-notice nyk-notice-warn">{result.note}</div>}
          <div>
            May read:{' '}
            {result.allowed.length ? (
              result.allowed.map((c) => (
                <Chip key={c} size="sm" className="nyk-tone-green">
                  {c}
                </Chip>
              ))
            ) : (
              <span className="nyk-ui-hint">no classified data</span>
            )}{' '}
            Hidden:{' '}
            {result.denied.length ? (
              result.denied.map((c) => (
                <Chip key={c} size="sm" className="nyk-tone-red">
                  {c}
                </Chip>
              ))
            ) : (
              <span className="nyk-ui-hint">nothing (passwords and licences never)</span>
            )}
          </div>
          {result.hiddenCount > 0 && (
            <details>
              <summary>
                {result.hiddenCount} requisite{result.hiddenCount === 1 ? '' : 's'} hidden from this user
              </summary>
              <div className="bd-access-hidden">
                {result.hidden.map((h) => (
                  <code key={h}>{h}</code>
                ))}
                {result.hiddenCount > result.hidden.length && <span className="nyk-ui-hint">…</span>}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------------------ profiles

interface ProfileDraft {
  isNew: boolean;
  key: string;
  name: string;
  description: string;
  classes: string[];
}

/** The PUT of a draft, or why it cannot be saved. */
export function profileProblem(d: ProfileDraft): string | null {
  if (d.isNew && !/^[a-z0-9_-]{1,64}$/.test(d.key.trim().toLowerCase()))
    return 'The key holds only letters, digits, - and _ (at most 64).';
  if (!d.name.trim()) return 'The name is required.';
  return null;
}

function ProfilesCard({
  api,
  profiles,
  known,
  canWrite,
  reload,
  onSaved,
}: {
  api: AccessApi;
  profiles: AccessProfile[];
  known: string[];
  canWrite: boolean;
  reload: () => Promise<unknown>;
  onSaved: (message: string) => void;
}) {
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!draft) return;
    const problem = profileProblem(draft);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      const key = draft.key.trim().toLowerCase();
      await api.putProfile(key, {
        name: draft.name.trim(),
        description: draft.description.trim(),
        classes: draft.classes,
      });
      onSaved(`Saved profile ${draft.name.trim()}`);
      setDraft(null);
      await reload();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (p: AccessProfile) => {
    try {
      await api.deleteProfile(p.key);
      onSaved(`Deleted profile ${p.name}`);
      await reload();
    } catch (e) {
      setError(messageOf(e));
    }
  };
  const all = draft?.classes.includes('*') ?? false;

  return (
    <div className="nyk-card" data-testid="access-profiles">
      <div className="nyk-card-head">
        <h2>Profiles</h2>
        {canWrite && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setError(null);
              setDraft({ isNew: true, key: '', name: '', description: '', classes: [] });
            }}
          >
            <Plus size={14} /> New profile
          </Button>
        )}
      </div>
      <ErrorBox error={draft ? null : error} />
      <TableScroller>
        <table className="nyk-ui-table" aria-label="Profiles">
          <thead>
            <tr>
              <th>Profile</th>
              <th>May read</th>
              <th>Description</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              <tr key={p.key} data-profile={p.key}>
                <td>
                  <b>{p.name}</b> <code className="nyk-ui-hint">{p.key}</code>{' '}
                  {p.origin === 'system' && (
                    <Chip size="sm" className="nyk-tone-grey">
                      shipped
                    </Chip>
                  )}
                </td>
                <td>
                  {classesOf(p, known).length ? (
                    classesOf(p, known).map((c) => (
                      <Chip key={c} size="sm">
                        {c}
                      </Chip>
                    ))
                  ) : (
                    <span className="nyk-ui-hint">no classified data</span>
                  )}
                </td>
                <td className="nyk-ui-hint">{p.description}</td>
                <td>
                  {canWrite && p.origin !== 'system' && (
                    <span className="bd-access-actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setError(null);
                          setDraft({
                            isNew: false,
                            key: p.key,
                            name: p.name,
                            description: p.description,
                            classes: p.classes,
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <ConfirmButton
                        size="sm"
                        variant="ghost"
                        message={`Delete profile ${p.name}? Groups mapped to it fall back to the default.`}
                        onConfirm={() => void remove(p)}
                      >
                        Delete
                      </ConfirmButton>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroller>
      {draft && (
        <Dialog
          open
          onClose={() => setDraft(null)}
          title={draft.isNew ? 'New profile' : `Profile ${draft.name}`}
          maxWidth={520}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button onClick={() => void save()} loading={busy}>
                Save
              </Button>
            </>
          }
        >
          <ErrorBox error={error} />
          {draft.isNew && (
            <TextField
              label="Key"
              value={draft.key}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              hint="what the mappings name, e.g. support"
              required
            />
          )}
          <TextField
            label="Name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            required
          />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
          <Switch
            label="Every class (also ones added later)"
            checked={all}
            onChange={(v) => setDraft({ ...draft, classes: v ? ['*'] : [] })}
          />
          {!all &&
            known.map((c) => (
              <Switch
                key={c}
                label={c}
                checked={draft.classes.includes(c)}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    classes: v ? [...draft.classes, c] : draft.classes.filter((x) => x !== c),
                  })
                }
              />
            ))}
        </Dialog>
      )}
    </div>
  );
}
